import React, { Fragment, useState, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import { Helmet } from "react-helmet";
import axios from "axios";

import Navbar from "../HomeScreen/Navbar";
import Loader from "../../layout/loader";
import MetaTag from "../../layout/MetaTag";

import {
  userAction,
  userLoginOtpVerifyAction,
  clearErrorAction,
  userLoadingAction,
} from "../../redux/actions/userAction";
import { mergeGuestCartAction } from "../../redux/actions/cartAction";

import "../../css/login.css";

import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

function Loginpage() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { error, loading, isAuthenticated, user } = useSelector(
    (state) => state.user
  );

  const [loginMethod, setLoginMethod] = useState("password"); // "password" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  // Compute safe redirect target
  const queryParams = new URLSearchParams(location.search);
  const rawRedirect = queryParams.get("redirect");
  let redirectTarget = "/userprofile";
  if (rawRedirect) {
    try {
      const decoded = decodeURIComponent(rawRedirect);
      redirectTarget = decoded.startsWith("/") ? decoded : `/${decoded}`;
    } catch (e) {
      redirectTarget = rawRedirect.startsWith("/") ? rawRedirect : `/${rawRedirect}`;
    }
  }

  const maskedEmail = (() => {
    const cleanEmail = email.trim();
    const [name, domain] = cleanEmail.split("@");
    if (!name || !domain) return cleanEmail;
    return `${name.slice(0, 2)}${name.length > 2 ? "***" : ""}@${domain}`;
  })();

  // Direct password-based login
  const handlePasswordLogin = (e) => {
    e.preventDefault();

    if (!email.trim() || !password) {
      return swal({
        title: "Validation Error",
        text: "Please enter both email and password.",
        icon: "info",
      });
    }

    dispatch(userAction(email.trim(), password));
  };

  // Request email OTP
  const handleRequestOtp = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password) {
      return swal({
        title: "Validation Error",
        text: "Please enter your email and password to receive an OTP.",
        icon: "info",
      });
    }

    try {
      setIsSendingOtp(true);
      const { data } = await axios.post("/api/Login/request-otp", {
        email: email.trim(),
        password,
      });

      setOtpSent(true);
      swal({
        title: "OTP Sent",
        text: data.message || "Login OTP sent to your email address.",
        icon: "success",
      });
    } catch (requestError) {
      swal({
        title: "Login Error",
        text:
          requestError.response?.data?.msg ||
          requestError.message ||
          "Unable to send login OTP.",
        icon: "error",
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = (e) => {
    e.preventDefault();

    if (!/^\d{4,8}$/.test(otp.trim())) {
      return swal({
        title: "Validation Error",
        text: "Please enter the valid OTP sent to your email.",
        icon: "info",
      });
    }

    dispatch(userLoginOtpVerifyAction(email.trim(), otp.trim()));
  };

  useEffect(() => {
    if (error) {
      swal({
        title: "Login Failed",
        text: error,
        icon: "error",
      });
      dispatch(clearErrorAction());
    }

    if (isAuthenticated) {
      // Merge guest items if any were stored in localStorage
      dispatch(mergeGuestCartAction());

      if (typeof user === "string") {
        dispatch(userLoadingAction());
      }

      history.push(redirectTarget);
    }
  }, [dispatch, error, history, isAuthenticated, redirectTarget, user]);

  return (
    <Fragment>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <MetaTag
        title={"Login | IMSHOP - Local Multi-Vendor Marketplace"}
        description="Login to your IMSHOP account to manage orders, wishlists, and track local store purchases."
      />

      {loading ? (
        <Loader />
      ) : (
        <Fragment>
          <Navbar />

          <main className="modern-login-page">
            <div className="login-left">
              <div className="brand-content">
                <span className="premium-label">IMSHOP Marketplace</span>

                <h1>Welcome back to your account</h1>

                <p>
                  Discover local stores, order for fast home delivery or store pickup,
                  track your active orders, and access your saved wishlist.
                </p>

                <div className="login-benefit-row">
                  <span>Saved Bag</span>
                  <span>Order Tracking</span>
                  <span>Local Pickup</span>
                </div>
              </div>
            </div>

            <div className="login-right">
              <div className="modern-login-card">
                <h2>Welcome Back</h2>

                {/* Login Method Toggle */}
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginBottom: "16px",
                    borderBottom: "1px solid #eee8df",
                    paddingBottom: "12px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMethod("password");
                      setOtpSent(false);
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      background: loginMethod === "password" ? "#111111" : "#fbfaf8",
                      color: loginMethod === "password" ? "#ffffff" : "#6f675e",
                      border: "1px solid #ded7cc",
                      borderRadius: "6px",
                      fontWeight: 800,
                      cursor: "pointer",
                      fontSize: "13px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    Direct Password Login
                  </button>

                  <button
                    type="button"
                    onClick={() => setLoginMethod("otp")}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      background: loginMethod === "otp" ? "#111111" : "#fbfaf8",
                      color: loginMethod === "otp" ? "#ffffff" : "#6f675e",
                      border: "1px solid #ded7cc",
                      borderRadius: "6px",
                      fontWeight: 800,
                      cursor: "pointer",
                      fontSize: "13px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    Email OTP Login
                  </button>
                </div>

                <p className="login-subtitle">
                  {loginMethod === "password"
                    ? "Enter your registered email and password to sign in immediately."
                    : otpSent
                    ? `Enter the OTP sent to ${maskedEmail}.`
                    : "Receive a one-time verification code on your registered email."}
                </p>

                {/* DIRECT PASSWORD LOGIN FORM */}
                {loginMethod === "password" && (
                  <form onSubmit={handlePasswordLogin}>
                    <div className="modern-input-box">
                      <EmailOutlinedIcon />
                      <input
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        required
                      />
                    </div>

                    <div className="modern-input-box" style={{ position: "relative" }}>
                      <LockOutlinedIcon />
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          color: "#8a8176",
                        }}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </button>
                    </div>

                    <div className="forgot-password-link">
                      <Link to="/login-forgot-password">Forgot Password?</Link>
                    </div>

                    <button
                      type="submit"
                      className="modern-login-btn"
                      disabled={loading}
                    >
                      {loading ? "Signing in..." : "Sign In"}
                      <ArrowForwardIcon />
                    </button>
                  </form>
                )}

                {/* OTP LOGIN FORM */}
                {loginMethod === "otp" && (
                  <Fragment>
                    {!otpSent ? (
                      <form onSubmit={handleRequestOtp}>
                        <div className="modern-input-box">
                          <EmailOutlinedIcon />
                          <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        </div>

                        <div className="modern-input-box">
                          <LockOutlinedIcon />
                          <input
                            type="password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                        </div>

                        <div className="forgot-password-link">
                          <Link to="/login-forgot-password">Forgot Password?</Link>
                        </div>

                        <button
                          type="submit"
                          className="modern-login-btn"
                          disabled={isSendingOtp}
                        >
                          {isSendingOtp ? "Sending OTP..." : "Send Login OTP"}
                          <ArrowForwardIcon />
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleVerifyOtp}>
                        <div className="modern-input-box">
                          <VerifiedUserOutlinedIcon />
                          <input
                            type="text"
                            placeholder="Enter OTP code"
                            maxLength="8"
                            inputMode="numeric"
                            value={otp}
                            onChange={(e) =>
                              setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))
                            }
                            required
                          />
                        </div>

                        <button
                          type="submit"
                          className="modern-login-btn"
                          disabled={loading}
                        >
                          {loading ? "Verifying..." : "Verify & Sign In"}
                          <ArrowForwardIcon />
                        </button>

                        <div className="login-otp-actions">
                          <button
                            type="button"
                            onClick={() => setOtpSent(false)}
                          >
                            Edit details
                          </button>
                          <button
                            type="button"
                            onClick={handleRequestOtp}
                            disabled={isSendingOtp}
                          >
                            {isSendingOtp ? "Sending..." : "Resend OTP"}
                          </button>
                        </div>
                      </form>
                    )}
                  </Fragment>
                )}

                <div className="login-footer">
                  <span>Don’t have an account?</span>
                  <Link to="/signin">Create Account</Link>
                </div>
              </div>
            </div>
          </main>
        </Fragment>
      )}
    </Fragment>
  );
}

export default Loginpage;
